import { ECONOMY } from '../constants/economy-registry';
/**
 * Module 126 (D1, D7) — total track puzzles solved before the avatar joins the
 * Home button inside a puzzle; the sign-in nudge (WP-126.6) starts at the same
 * count, so the in-puzzle "Sign in" and the nudge arrive together.
 */
export const PROFILE_IN_PUZZLE_SOLVES = 10;
/** The default avatar of every new player. */
export const DEFAULT_AVATAR_ID = 'sunny';
/** D9 — the three free avatars, in picker order. All Sunny yellow, static. */
export const AVATARS = [
    { id: 'sunny', tier: 'free', nameKey: 'blockjam.avatar.name.sunny' },
    { id: 'giggle', tier: 'free', nameKey: 'blockjam.avatar.name.giggle' },
    { id: 'winky', tier: 'free', nameKey: 'blockjam.avatar.name.winky' },
    /** Module 126 (D14) — earned only by signing in (the Welcome gift); never sold. */
    { id: 'nimbus', tier: 'earned', nameKey: 'blockjam.avatar.name.nimbus' },
];
/**
 * Module 152 (V2, V5) — the 33 coin avatars, catalogue order, each with its
 * wave (Wave 1 = 20 at launch, Wave 2 = 13 later) and group. Prices live in the
 * registry (`ECONOMY.avatars`), never here.
 */
const WAVE_1 = new Set([
    'keymaster',
    'wild',
    'boom',
    'royal',
    'kitty',
    'fox',
    'panda',
    'bunny',
    'rosie',
    'starlight',
    'fairy',
    'shades',
    'ace',
    'ninja',
    'unicorn',
    'dragon',
    'robot',
    'ghost',
    'dj',
    'party',
]);
const COIN_GROUPS = [
    ['heroes', ['keymaster', 'hardhat', 'wild', 'boom', 'volt', 'royal']],
    ['animals', ['kitty', 'fox', 'panda', 'bunny', 'frog', 'owl']],
    ['beautiful', ['rosie', 'starlight', 'bloom', 'pearl', 'fairy']],
    ['handsome', ['ace', 'shades', 'captain', 'dapper', 'ninja', 'knight']],
    ['fantasy', ['wizard', 'robot', 'alien', 'dragon', 'unicorn', 'ghost']],
    ['fun', ['party', 'dj', 'sleepy', 'blaze']],
];
for (const [group, ids] of COIN_GROUPS) {
    for (const id of ids) {
        AVATARS.push({
            id,
            tier: 'coins',
            nameKey: `blockjam.avatar.name.${id}`,
            wave: WAVE_1.has(id) ? 1 : 2,
            group,
        });
    }
}
/** Module 152 — a coin avatar's price, from the registry; 0 for anything else. */
export function avatarPrice(id) {
    const tier = ECONOMY.avatars.tiers[id];
    return tier ? ECONOMY.avatars.tierPrices[tier] : 0;
}
/** Module 152 — a coin avatar's tier name (`common` … `mythic`), or undefined. */
export function avatarTierName(id) {
    return ECONOMY.avatars.tiers[id];
}
/** Module 152 (V5) — coin avatars released so far, cheapest first (ties: catalogue order). */
export function releasedCoinAvatars(waveLive = ECONOMY.avatars.waveLive) {
    return AVATARS.filter((a) => a.tier === 'coins' && (a.wave ?? 1) <= waveLive)
        .map((a, i) => ({ a, i }))
        .sort((x, y) => avatarPrice(x.a.id) - avatarPrice(y.a.id) || x.i - y.i)
        .map(({ a }) => a);
}
/** Module 126 (D14) — the Welcome gift's avatar. */
export const WELCOME_AVATAR_ID = 'nimbus';
/** An id the save may hold: short, lower-case, url-safe. */
export const AVATAR_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;
/** A tampered save cannot grow the owned list without bound. */
export const AVATAR_OWNED_CAP = 200;
export function avatarDef(id) {
    return AVATARS.find((a) => a.id === id);
}
/** The state of a save that never chose: Sunny, nothing extra. */
export function defaultAvatarState() {
    return { chosen: DEFAULT_AVATAR_ID, owned: [] };
}
/** Does this player own `id`? Free avatars always; others only when got. */
export function ownsAvatar(state, id) {
    const def = avatarDef(id);
    if (def?.tier === 'free')
        return true;
    return state.owned.includes(id);
}
/**
 * The avatar to DRAW: the chosen one if this build knows it and the player owns
 * it, else Sunny. The save itself is never rewritten by this.
 */
export function displayedAvatar(state) {
    const def = avatarDef(state.chosen);
    if (def && ownsAvatar(state, def.id))
        return def;
    return avatarDef(DEFAULT_AVATAR_ID);
}
/**
 * Pick `id`. Returns the new state, or `null` when it can't be chosen (unknown
 * here, or not owned), so the caller writes nothing.
 */
export function chooseAvatar(state, id) {
    if (!avatarDef(id) || !ownsAvatar(state, id))
        return null;
    if (state.chosen === id)
        return state;
    return { chosen: id, owned: state.owned };
}
/** Picker order: every avatar the player owns, registry order. */
export function ownedAvatars(state) {
    return AVATARS.filter((a) => ownsAvatar(state, a.id));
}
/**
 * Rebuild from raw save JSON, field by field (a field the validator doesn't
 * name is silently dropped, the Module 81 lesson). A bad `chosen` falls back to
 * Sunny; bad `owned` entries are skipped; the save is never rejected for this.
 */
export function avatarStateFromRaw(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return undefined;
    const raw = value;
    const chosen = typeof raw.chosen === 'string' && AVATAR_ID_PATTERN.test(raw.chosen)
        ? raw.chosen
        : DEFAULT_AVATAR_ID;
    const owned = [];
    if (Array.isArray(raw.owned)) {
        for (const id of raw.owned) {
            if (owned.length >= AVATAR_OWNED_CAP)
                break;
            if (typeof id !== 'string' || !AVATAR_ID_PATTERN.test(id))
                continue;
            if (avatarDef(id)?.tier === 'free' || owned.includes(id))
                continue;
            owned.push(id);
        }
    }
    return { chosen, owned };
}
/** Module 152 — can this coin avatar be unlocked in this build, right now? */
export function isUnlockable(state, id, waveLive = ECONOMY.avatars.waveLive) {
    const def = avatarDef(id);
    return (def?.tier === 'coins' &&
        (def.wave ?? 1) <= waveLive &&
        avatarPrice(id) > 0 &&
        !ownsAvatar(state, id) &&
        state.owned.length < AVATAR_OWNED_CAP);
}
/**
 * Module 152 (WP-152.2) — the state after unlocking `id`: owned and shown at
 * once. `null` when it can't be unlocked (unknown, not for coins, not released,
 * already owned), so the caller spends nothing and writes nothing: owning it
 * already is what makes a second tap free.
 */
export function unlockAvatar(state, id, waveLive = ECONOMY.avatars.waveLive) {
    if (!isUnlockable(state, id, waveLive))
        return null;
    return { chosen: id, owned: [...state.owned, id] };
}
/** Module 152 (WP-152.3) — "Collected n / m": owned avatars of all released, free ones included. */
export function avatarCollection(state, waveLive = ECONOMY.avatars.waveLive) {
    // An earned avatar (Nimbus) counts once the player has it: it is never for
    // sale, so a total that included it would read as one nobody can reach.
    const released = AVATARS.filter((a) => a.tier === 'free' ||
        (a.tier === 'coins' && (a.wave ?? 1) <= waveLive) ||
        (a.tier === 'earned' && state.owned.includes(a.id)));
    const owned = released.filter((a) => ownsAvatar(state, a.id)).length;
    return { owned, total: released.length };
}
/**
 * Module 152 (WP-152.3) — coin avatars of a wave newer than `lastSeenWave` and
 * already released: they wear NEW, and one home card announces them.
 */
export function newAvatarIds(lastSeenWave, waveLive = ECONOMY.avatars.waveLive) {
    return AVATARS.filter((a) => a.tier === 'coins' && (a.wave ?? 1) > lastSeenWave && (a.wave ?? 1) <= waveLive).map((a) => a.id);
}
